import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { API_ENDPOINTS } from "../config/constants";
import SpotterLicense from "./SpotterLicense";
import VehicleLink from "./VehicleLink";
import { toast } from "react-hot-toast";

const ProfilePage = ({ currentUser, usernameChanged }) => {
  const { t } = useTranslation();
  const { username: urlUsername } = useParams();
  const [username, setUsername] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [profile, setProfile] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshingSnapshot, setIsRefreshingSnapshot] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(0);
  const [refreshMessage, setRefreshMessage] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isUsernameFormOpen, setIsUsernameFormOpen] = useState(false);
  const [sort, setSort] = useState("recent");

  // Loading messages for license refresh
  const refreshMessages = [
    t("profile.refresh_messages.starting"),
    t("profile.refresh_messages.launching_browser"),
    t("profile.refresh_messages.loading_profile"),
    t("profile.refresh_messages.capturing_license"),
    t("profile.refresh_messages.optimizing_image"),
    t("profile.refresh_messages.uploading"),
    t("profile.refresh_messages.finalizing"),
  ];

  // Determine if we're viewing our own profile or someone else's
  const isOwnProfile =
    !urlUsername || (currentUser && currentUser.username === urlUsername);
  const targetUser = isOwnProfile ? currentUser : profile?.user;
  const displayUsername = targetUser?.username;

  // Reset state when switching profiles
  useEffect(() => {
    setLoading(true);
    if (!isOwnProfile) {
      setProfile(null);
      setVehicles([]);
      setPage(1);
      setHasMore(true);
      fetchProfile();
    } else {
      setProfile({ user: currentUser });
      setVehicles([]);
      setPage(1);
      setHasMore(true);
      setLoading(false);
    }
  }, [urlUsername, currentUser, isOwnProfile]);

  // Fetch vehicles when profile is loaded
  useEffect(() => {
    if (displayUsername) {
      fetchVehicles(1);
    }
  }, [displayUsername]);

  // Reset page when sort changes
  useEffect(() => {
    if (displayUsername) {
      setPage(1);
      setVehicles([]);
      fetchVehicles(1);
    }
  }, [sort]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINTS.USERS}/${urlUsername}`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("User not found");
      }

      const data = await response.json();
      setProfile(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicles = async (pageNum = 1) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_ENDPOINTS.EXPLORE_VEHICLES}?page=${pageNum}&limit=12&sort=${sort}&filter=user&username=${displayUsername}`,
        {
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch vehicles");
      }

      const data = await response.json();

      if (pageNum === 1) {
        setVehicles(data.vehicles);
      } else {
        setVehicles((prev) => {
          // Create a Set of existing IDs to avoid duplicates
          const existingIds = new Set(prev.map((v) => v._id));
          // Only add vehicles that don't already exist
          const newVehicles = data.vehicles.filter(
            (v) => !existingIds.has(v._id)
          );
          return [...prev, ...newVehicles];
        });
      }

      setHasMore(data.pagination.page < data.pagination.totalPages);
      setPage(pageNum);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    if (!hasMore || loading) return;
    fetchVehicles(page + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_ENDPOINTS.CHANGE_USERNAME}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ username }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.msg || "Error changing username");
      }

      setSuccess(true);
      setError("");
    } catch (err) {
      setError(err.message || "An error occurred");
      setSuccess(false);
    }
  };

  const handleCopyProfileLink = async () => {
    try {
      const profileUrl = `${window.location.origin}/u/${targetUser.username}`;
      await navigator.clipboard.writeText(profileUrl);
      toast.success(t("profile.link_copied"));

      // Then use the Web Share API if available
      if (navigator.share) {
        try {
          await navigator.share({
            title: t("profile.share_title", { username: targetUser.username }),
            text: t("profile.share_text", { username: targetUser.username }),
            url: profileUrl,
          });
        } catch (shareError) {
          // Ignore if user cancels share
          if (shareError.name !== "AbortError") {
            console.error("Error with native share:", shareError);
          }
        }
      }
    } catch (error) {
      console.error("Error copying link:", error);
      toast.error(t("profile.copy_error"));
    }
  };

  const handleRefreshLicenseSnapshot = async () => {
    try {
      setIsRefreshingSnapshot(true);
      setRefreshProgress(0);
      setRefreshMessage(refreshMessages[0]);

      // Trigger the snapshot regeneration (returns immediately)
      const response = await fetch(
        `${API_ENDPOINTS.REGENERATE_LICENSE_SNAPSHOT}/${targetUser.username}`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to start license snapshot regeneration");
      }

      // Show immediate feedback that process started
      toast.success(t("profile.snapshot_started"));

      // Simulate realistic progress with messages over ~20-30 seconds
      const progressInterval = setInterval(() => {
        setRefreshProgress((prev) => {
          const newProgress = Math.min(prev + 1, refreshMessages.length - 1);
          setRefreshMessage(refreshMessages[newProgress]);

          // If we've reached the end, finish up
          if (newProgress >= refreshMessages.length - 1) {
            clearInterval(progressInterval);
            setTimeout(() => {
              setRefreshMessage(t("profile.refresh_messages.complete"));
              toast.success(t("profile.snapshot_refreshed"));

              // Keep the success message visible for a moment
              setTimeout(() => {
                setIsRefreshingSnapshot(false);
                setRefreshProgress(0);
                setRefreshMessage("");
              }, 3000);
            }, 2000);
          }

          return newProgress;
        });
      }, 4000); // Change message every 4 seconds for more realistic timing
    } catch (error) {
      console.error("Error refreshing license snapshot:", error);
      toast.error(t("profile.snapshot_refresh_error"));
      setIsRefreshingSnapshot(false);
      setRefreshProgress(0);
      setRefreshMessage("");
    }
  };

  if (error) {
    return (
      <div className="container mx-auto px-4 py-4">
        <div className="bg-red-50 text-red-700 p-4 rounded-md">{error}</div>
      </div>
    );
  }

  if (!targetUser && loading) {
    return (
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!targetUser && !loading) {
    return (
      <div className="container mx-auto px-4 py-4">
        <div className="bg-yellow-50 text-yellow-700 p-4 rounded-md">
          {t("profile.user_not_found")}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 py-2 max-w-5xl">
      <div className="space-y-8">
        {/* Spotter's License */}
        {targetUser && (
          <div className="relative pt-8">
            <SpotterLicense
              user={targetUser}
              isEditable={isOwnProfile}
              key={targetUser._id}
            />

            {/* Share Actions */}
            <div className="absolute top-[-15px] right-[-10px] flex gap-2">
              {/* Copy Link Button */}
              <button
                onClick={handleCopyProfileLink}
                className="bg-white dark:bg-gray-700 rounded-full p-2 shadow-md text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:shadow-lg transition-all"
                title={t("profile.copy_link")}
              >
                <svg
                  className="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                </svg>
              </button>

              {/* Refresh Snapshot Button */}
              <button
                onClick={handleRefreshLicenseSnapshot}
                disabled={isRefreshingSnapshot}
                className="bg-white dark:bg-gray-700 rounded-full p-2 shadow-md text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                title={t("profile.refresh_snapshot")}
              >
                {isRefreshingSnapshot ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500 dark:border-blue-400"></div>
                ) : (
                  <svg
                    className="h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            </div>

            {/* Progress Indicator */}
            {isRefreshingSnapshot && (
              <div className="absolute top-12 right-0 bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900/50 p-3 min-w-[200px] z-10 border dark:border-gray-700">
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500 dark:border-blue-400"></div>
                  <span>{refreshMessage}</span>
                </div>
                <div className="mt-2 bg-gray-200 dark:bg-gray-700 rounded-full h-1">
                  <div
                    className="bg-blue-500 dark:bg-blue-400 h-1 rounded-full transition-all duration-300"
                    style={{
                      width: `${
                        ((refreshProgress + 1) / refreshMessages.length) * 100
                      }%`,
                    }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* All Spotted Vehicles */}
        <div className="bg-white rounded-lg shadow p-4 dark:bg-gray-800">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">
              {t("profile.spotted_vehicles")}
            </h2>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 dark:text-gray-400">
                {t("profile.sort_by")}
              </label>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400"
              >
                <option value="recent">
                  {t("profile.sort_options.recent")}
                </option>
                <option value="views">{t("profile.sort_options.views")}</option>
                <option value="likes">{t("profile.sort_options.likes")}</option>
                <option value="images">
                  {t("profile.sort_options.images")}
                </option>
                <option value="discussions">
                  {t("profile.sort_options.discussions")}
                </option>
              </select>
            </div>
          </div>
          {loading && vehicles.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {t("profile.loading")}
            </div>
          ) : vehicles.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-4">
                {vehicles.map((vehicle) => (
                  <VehicleLink
                    key={vehicle._id}
                    vehicle={vehicle}
                    variant="card"
                  />
                ))}
              </div>
              {hasMore && (
                <div className="mt-6 text-center">
                  <button
                    onClick={loadMore}
                    className="bg-primary-500 text-white px-4 py-2 rounded-md hover:bg-primary-600 transition-colors dark:bg-primary-600 dark:hover:bg-primary-700"
                    disabled={loading}
                  >
                    {loading ? t("profile.loading") : t("profile.load_more")}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              {isOwnProfile
                ? t("profile.no_vehicles_own")
                : t("profile.no_vehicles_other")}
            </div>
          )}
        </div>

        {/* Community Guidelines Status */}
        {isOwnProfile && (
          <div className="bg-white rounded-lg shadow p-6 dark:bg-gray-800">
            <h3 className="text-lg font-medium mb-2">
              {t("profile.guidelines_status.title")}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {currentUser.hasAcceptedGuidelines
                ? t("profile.guidelines_status.accepted")
                : t("profile.guidelines_status.not_accepted")}
            </p>
          </div>
        )}

        {/* Username Change Form */}
        {isOwnProfile && !usernameChanged && (
          <div className="bg-white rounded-lg shadow p-6 dark:bg-gray-800">
            <button
              onClick={() => setIsUsernameFormOpen(!isUsernameFormOpen)}
              className="w-full flex justify-between items-center text-left dark:text-gray-400"
            >
              <h2 className="text-xl font-bold dark:text-gray-100">
                {t("profile.username.title")}
              </h2>
              <svg
                className={`w-6 h-6 transform transition-transform ${
                  isUsernameFormOpen ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {isUsernameFormOpen && (
              <div className="mt-4">
                {success && (
                  <div className="bg-green-50 text-green-700 p-4 rounded-md mb-4 dark:bg-green-900 dark:text-green-100">
                    {t("profile.username.success")}
                  </div>
                )}
                {error && (
                  <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4 dark:bg-red-900 dark:text-red-100">
                    {error}
                  </div>
                )}

                {!success && (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                        {t("profile.username.current")}{" "}
                        <span className="text-gray-500 dark:text-gray-400">
                          {currentUser.username}
                        </span>
                      </label>
                      <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                        {t("profile.username.new")}
                      </label>
                      <input
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                        minLength={3}
                        maxLength={30}
                        className="w-full border border-gray-300 rounded-md p-2 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                      />
                      <p className="text-sm text-gray-500 mt-1 dark:text-gray-400">
                        {t("profile.username.warning")}
                      </p>
                    </div>
                    <button
                      type="submit"
                      className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors"
                    >
                      {t("profile.username.submit")}
                    </button>
                  </form>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
